import "./receiptprint.scss";
import * as React from "react";
import { connect, type ConnectedProps } from "react-redux";
import { type ThunkDispatch } from "redux-thunk";
import { type UnwrapPromise } from "../../../../types/internal/utility";
import { type LocationState } from "../../../Actions/General/LocationActions";
import {
    changeSellScreenActionMode,
    changeSellScreenSearchResultsTo,
    changeSellScreenSummaryMode,
    type GiftCardUsedType,
    sendEmailReceipt,
} from "../../../Actions/SellScreen/SellScreenActions";
import { PREVIOUS_SALE_PRINTED } from "../../../Actions/SellScreen/SellScreenConstants";
import Currency from "../../../Common/Misc/Currency";
import ReceiptPrint, { type ReceiptPrintActionType } from "../../../Common/Receipts/ReceiptPrint";
import { determineDataSource } from "../../../Data/DataStores/DataSource";
import { type SaleState } from "../../../Reducers/SellScreen/SellScreenSaleReducerTypes";
import { type RootState } from "../../../Reducers/ShopfrontReducers";
import ReceiptHelper, { type AdditionalSaleData, type ReceiptSaleData } from "../../../Utilities/Domain/ReceiptHelper";
import { withPermissions } from "../../../Utilities/Domain/Users/Permissions";
import { fireEventType } from "../../../Utilities/Generic/ActionsHelper";
import { compare } from "../../../Utilities/Generic/Objects";
import { CancellablePromise } from "../../../Utilities/Generic/Promises";
import { getOption } from "../../../Utilities/Platform/Options/Options";
import { PrintContext } from "../../../Utilities/UI/PrintHelper";

function mapStateToProps(state: RootState): {
    location: LocationState;
    training: boolean;
} {
    return {
        location: state.location,
        training: state.information.training,
    };
}

const connector = connect(mapStateToProps);

interface Props extends ConnectedProps<typeof connector> {
    dispatch: ThunkDispatch<any, any, any>;
    sale: (SaleState & { giftCardsUsed?: Array<GiftCardUsedType>; }) | false;
    logout?: boolean;
    history?: boolean;
    dialog?: boolean;
    permissions: {
        history_modify_sale: boolean;
    };
    additionalSaleData?: AdditionalSaleData;
}

interface State {
    loading: boolean;
    data: false | ReceiptSaleData;
    error: boolean;
    print: boolean;
    receipt: string | null;
}

export class SaleReceiptPrint extends React.Component<Props, State> {
    public static contextType = PrintContext;
    protected dataPromise: null | CancellablePromise<[
        UnwrapPromise<ReturnType<typeof ReceiptHelper.createReceiptDataFromSaleState>>,
        UnwrapPromise<ReturnType<typeof SaleReceiptPrint.prototype.getDefaultReceipt>>,
    ]>;

    constructor(props: Props) {
        super(props);

        this.state = {
            loading: true,
            data   : false,
            error  : false,
            print  : false,
            receipt: getOption("register_default_receipt_template"),
        };

        this.dataPromise = null;

        this.handleDoneClick = this.handleDoneClick.bind(this);
        this.handleEmailClick = this.handleEmailClick.bind(this);
        this.handleAfterPrint = this.handleAfterPrint.bind(this);
        this.handleAddCustomerClick = this.handleAddCustomerClick.bind(this);
        this.getDefaultReceipt = this.getDefaultReceipt.bind(this);
    }

    public componentDidMount(): void {
        void this.prepareSaleData();
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if(prevProps.sale === false && this.props.sale !== false) {
            void this.prepareSaleData();

            return;
        } else if(prevProps.sale === false || this.props.sale === false) {
            return;
        }

        const { printReceipt: oldPrint, ...prevSale } = prevProps.sale;
        const { printReceipt: newPrint, ...currSale } = this.props.sale;

        if(!compare(prevSale, currSale)) {
            void this.prepareSaleData();
        } else if(oldPrint && !newPrint) {
            // prepareSaleData updates print
            this.setState({
                print: false,
            });
        }
    }

    public componentWillUnmount(): void {
        if(this.dataPromise) {
            this.dataPromise.cancel();
        }
    }

    protected getDefaultReceipt(): Promise<string | null> {
        if(!this.props.sale) {
            return Promise.resolve(null);
        }

        const saleCustomer = this.props.sale.customer;

        if(saleCustomer) {
            return determineDataSource()
                .then(async source => {
                    const customer = await source.customers.get(saleCustomer.uuid);

                    if(customer.receipt) {
                        try {
                            const receipt = await source.receipts.get(customer.receipt);

                            return receipt.uuid;
                        } catch {
                            return getOption("register_default_receipt_template");
                        }
                    }

                    return getOption("register_default_receipt_template");
                })
                .catch(err => {
                    console.error("Failed to load ", err);

                    return getOption("register_default_receipt_template");
                });
        } else {
            return Promise.resolve(getOption("register_default_receipt_template"));
        }
    }

    protected prepareSaleData(): Promise<void> {
        if(this.props.sale === false) {
            return Promise.resolve();
        }

        this.setState({
            loading: true,
        });

        if(this.dataPromise) {
            this.dataPromise.cancel();
        }

        const id = Date.now();

        this.dataPromise = new CancellablePromise(Promise.all([
            ReceiptHelper.createReceiptDataFromSaleState(
                this.props.sale as Required<SaleState> & { giftCardsUsed?: Array<GiftCardUsedType>; },
                this.props.additionalSaleData
            ),
            this.getDefaultReceipt(),
        ]));

        return this.dataPromise
            .promise
            .then(([ data, receipt ]) => {
                if(this.props.sale === false) {
                    return;
                }

                this.dataPromise = null;
                this.setState({
                    loading: false,
                    print  : !!(this.props.sale.printReceipt),
                    receipt,
                    data,
                });
            })
            .catch(err => {
                if(!err.cancelled) {
                    console.error(`Error trying to create receipt data, ID: ${id}`, err);
                }
            });
    }

    protected handleAddCustomerClick(): void {
        if(!this.props.sale || this.props.sale.customer) {
            return;
        }

        this.props.dispatch(changeSellScreenActionMode("customers"));
        this.props.dispatch(changeSellScreenSearchResultsTo("previous-sale"));
    }

    protected handleAfterPrint(): void {
        if(!this.props.history) {
            this.props.dispatch(fireEventType(PREVIOUS_SALE_PRINTED));
        }
    }

    protected handleEmailClick(): void {
        this.props.dispatch(sendEmailReceipt({
            overLogin: !!this.props.logout,
        }));
    }

    protected handleDoneClick(): void {
        this.props.dispatch(changeSellScreenSummaryMode("transaction"));
    }

    public render(): React.ReactNode {
        const classes = [ "sale-receipt-print" ];

        if(!this.props.sale) {
            return (
                <div className={classes.join(" ")}>
                    No Previous sale
                </div>
            );
        }

        let showHeader = true;
        let showDone = true;
        let showEmail = true;
        let showCustomer = this.props.permissions.history_modify_sale;

        if(this.props.logout) {
            classes.push("logout");
            showHeader = !!this.props.sale.totals.change;
            showCustomer = false;
            showDone = false;
        }

        if(this.props.history) {
            classes.push("history");
            showHeader = false;
            showDone = false;
            showCustomer = false;
            showEmail = false;
        }

        if(this.props.sale.customer) {
            showCustomer = false;
        }

        let header;

        if(this.props.sale.totals.change) {
            classes.push("has-change");

            header = (
                <React.Fragment>
                    <div className="sale-change">Change</div>
                    <Currency value={this.props.sale.totals.change} />
                </React.Fragment>
            );
        } else if(this.props.sale.totals.cashout) {
            header = (
                <React.Fragment>
                    <div className="sale-cashout">Cash Out</div>
                    <Currency value={this.props.sale.totals.cashout} />
                </React.Fragment>
            );
        } else if(this.props.sale.status?.toLowerCase() === "parked") {
            header = "Sale Parked";
        } else {
            header = "Sale Complete";
        }

        const actions: Array<ReceiptPrintActionType> = [];

        if(showCustomer) {
            actions.push({
                icon   : "user-plus",
                label  : "Add Customer",
                onClick: this.handleAddCustomerClick,
            });
        }

        if(showEmail) {
            actions.push({
                icon   : "envelope",
                label  : "Email",
                onClick: this.handleEmailClick,
            });
        }

        return (
            <div className={classes.join(" ")}>
                {showHeader && (
                    <div className="receipt-header">
                        {header}
                    </div>
                )}
                {(!this.state.loading && this.state.data) && (
                    <ReceiptPrint
                        dialog={this.props.dialog}
                        actions={actions}
                        data={this.state.data}
                        print={this.state.print}
                        onPrint={this.handleAfterPrint}
                        defaultReceipt={this.state.receipt === null ? undefined : this.state.receipt}
                        onComplete={(!this.props.dialog && showDone) ? this.handleDoneClick : undefined}
                    />
                )}
            </div>
        );
    }
}

export default connector(withPermissions(SaleReceiptPrint, [ "history_modify_sale" ]));
